import { useState } from 'react'
import { Input, InputTextArea, Row, useForm } from '@/components/forms'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'
import type { MeaningRun, NavCall, RankedResult } from '@/lib/agents/workers/cmssearch'
import { workerRegistry } from '@/lib/agents/registry'
import { createModel } from '@/lib/data/model'
import { NodeProps } from '@xyflow/react'
import { ChevronDown, ChevronRight, DatabaseZap, ExternalLink, Sparkles } from "lucide-react"
import { WorkerLabeledHandle } from '../handles'
import { useWorker } from '../hooks'
import { MemoizedWorker } from '../memoizedworkers'
import { NodeLayout } from './node'
import { ConditionHandler } from '../condition'

const { cmssearch } = workerRegistry
cmssearch.icon = DatabaseZap

const model = createModel({
  fields: {
    maxResults: { title: "Max Results", type: "number" },
    locale: { title: "Preferred Locale", type: "string" },
    toolDescription: { title: "Tool Description", type: "string" },
  },
})

function Parameters({ worker }: { worker: CMSSearchWorker }) {

  const [searchArticles, setSearchArticles] = useState(worker.parameters.searchArticles !== false)
  const [searchServices, setSearchServices] = useState(worker.parameters.searchServices !== false)
  const [searchSites, setSearchSites] = useState(worker.parameters.searchSites !== false)
  const [byMeaning, setByMeaning] = useState(worker.parameters.searchMode !== "keyword")

  const { form, m, watch } = useForm(model, {
    values: {
      maxResults: worker.parameters.maxResults ?? 5,
      locale: worker.parameters.locale ?? "",
      toolDescription: worker.parameters.toolDescription ?? "",
    },
  })

  watch((value, { name }) => {
    if (name === "maxResults") worker.parameters.maxResults = value.maxResults
    if (name === "locale") worker.parameters.locale = value.locale
    if (name === "toolDescription") worker.parameters.toolDescription = value.toolDescription
  })

  function onArticlesChange(s: boolean) {
    setSearchArticles(s)
    worker.parameters.searchArticles = s
  }

  function onServicesChange(s: boolean) {
    setSearchServices(s)
    worker.parameters.searchServices = s
  }

  function onSitesChange(s: boolean) {
    setSearchSites(s)
    worker.parameters.searchSites = s
  }

  function onMeaningChange(s: boolean) {
    setByMeaning(s)
    worker.parameters.searchMode = s ? "hybrid" : "keyword"
  }

  return <form.context>
    <div className='px-2 pb-2 nodrag w-full flex flex-col'>

      <Row className='py-1 flex items-center'>
        <Label htmlFor="searchArticles" className='flex-1'>Articles</Label>
        <Switch id="searchArticles" onCheckedChange={onArticlesChange} checked={searchArticles} />
      </Row>
      <Row className='py-1 flex items-center'>
        <Label htmlFor="searchServices" className='flex-1'>Service Map</Label>
        <Switch id="searchServices" onCheckedChange={onServicesChange} checked={searchServices} />
      </Row>
      <Row className='py-1 flex items-center'>
        <Label htmlFor="searchSites" className='flex-1'>Site Config</Label>
        <Switch id="searchSites" onCheckedChange={onSitesChange} checked={searchSites} />
      </Row>

      {!searchArticles && !searchServices && !searchSites && (
        <p className='text-[10px] text-destructive py-1'>
          No area is reachable. Turn on at least one.
        </p>
      )}

      <Row className='py-2'>
        <Input span={6} field={m.maxResults} />
        <Input span={6} field={m.locale} />
      </Row>

      <Row className='py-1 flex items-center'>
        <Label htmlFor="searchByMeaning" className='flex-1' title="Also match by meaning when the team has turned on Search by meaning in the Smart CMS">Search by meaning</Label>
        <Switch id="searchByMeaning" onCheckedChange={onMeaningChange} checked={byMeaning} />
      </Row>

      <Row className='py-2'>
        <InputTextArea span={12} field={m.toolDescription} rows={3} />
      </Row>

    </div>
  </form.context>

}

// On each search call: whether meaning search ran, and if not, why
function SearchMode({ run }: { run: MeaningRun }) {
  if (run.used) {
    return (
      <span
        title={`Searched by words and meaning · ${run.matches} record${run.matches === 1 ? "" : "s"} found by meaning`}
        className='flex shrink-0 items-center gap-0.5 rounded bg-violet-50 px-1 text-[9px] font-medium text-violet-700'
      >
        <Sparkles className='h-2 w-2' />
        meaning · {run.matches}
      </span>
    )
  }
  return (
    <span
      title={`Words only: ${run.skipped}`}
      className={cn('shrink-0 rounded bg-gray-100 px-1 text-[9px]', run.skipped?.startsWith('meaning search failed') ? 'text-destructive' : 'text-gray-500')}
    >
      words only
    </span>
  )
}

// Text as the agent received it
function ResultText({ text, failed }: { text: string; failed?: boolean }) {
  return (
    <pre className={cn(
      'whitespace-pre-wrap break-words rounded border border-gray-200 bg-gray-50 p-1.5 text-[10px] leading-snug',
      'select-text cursor-text',
      failed ? 'text-destructive' : 'text-gray-700',
    )}>
      {text}
    </pre>
  )
}

// The whole search response, closed by default: each sent result opens on its own above
function FullResponse({ text, failed }: { text: string; failed?: boolean }) {
  const [open, setOpen] = useState(false)
  return (
    <div>
      <button type='button' onClick={() => setOpen(!open)} className='flex items-center gap-1 text-[10px] text-gray-500 hover:text-gray-800'>
        {open ? <ChevronDown className='h-2.5 w-2.5' /> : <ChevronRight className='h-2.5 w-2.5' />}
        Full response sent to the agent
      </button>
      {open && <div className='pt-1'><ResultText text={text} failed={failed} /></div>}
    </div>
  )
}

// A search's results in rank order: the ones within Max Results went to the agent with their full text
function RankedResults({ results }: { results: RankedResult[] }) {
  const [openRow, setOpenRow] = useState<number | null>(null)
  const sent = results.filter(r => r.sent)
  const rest = results.filter(r => !r.sent)
  const row = (r: RankedResult, i: number) => (
    <div key={i}>
    <button
      type='button'
      disabled={!r.text}
      onClick={() => setOpenRow(openRow === i ? null : i)}
      className={cn('flex w-full items-center gap-1.5 text-left text-[10px]', r.text ? 'hover:bg-white' : 'cursor-default opacity-50')}
    >
      {r.text
        ? (openRow === i ? <ChevronDown className='h-2.5 w-2.5 shrink-0 text-gray-400' /> : <ChevronRight className='h-2.5 w-2.5 shrink-0 text-gray-400' />)
        : <span className='w-2.5 shrink-0' />}
      <span className='w-3 shrink-0 text-right tabular-nums text-gray-400'>{i + 1}</span>
      <span className='w-7 shrink-0 tabular-nums font-medium text-violet-700' title='Similarity to the query'>
        {r.similarity !== undefined ? r.similarity.toFixed(2) : ''}
      </span>
      <span className='min-w-0 grow truncate text-gray-700'>{r.title}</span>
      <span className='shrink-0 text-gray-400'>
        {r.words && r.similarity !== undefined ? 'words + meaning' : r.words ? 'words' : 'meaning'}
      </span>
    </button>
    {openRow === i && r.text && <div className='py-1 pl-4'><ResultText text={r.text} /></div>}
    </div>
  )
  return (
    <div className='space-y-0.5 rounded border border-gray-200 bg-gray-50 p-1.5'>
      <p className='pb-0.5 text-[10px] font-medium text-gray-700'>Sent to the agent ({sent.length}, full text; click one to see it)</p>
      {sent.map(row)}
      {rest.length > 0 && (
        <>
          <p className='pt-1 text-[10px] text-gray-400'>Below Max Results, not sent</p>
          {rest.map((r, i) => row(r, sent.length + i))}
        </>
      )}
    </div>
  )
}

function SearchingByMeaning() {
  return (
    <span className='flex items-center gap-1 text-[10px] text-violet-600'>
      <Sparkles className='h-2.5 w-2.5 animate-pulse' />
      Searching by meaning…
    </span>
  )
}

function cmsHref(path: string): string | null {
  const parts = path.split("/").filter(Boolean)
  const [area] = parts
  const last = parts[parts.length - 1]
  if (!area || !last || !last.includes(":")) return null
  if (area !== "articles" && area !== "services") return null

  const kind = last.slice(0, last.indexOf(":"))
  // Only kinds the page can actually select. A category or section selects
  // fine in the article tree; a service or provider in the service map.
  const selectable = area === "articles"
    ? ["category", "section", "article"]
    : ["service", "provider", "country", "region", "city", "category"]
  if (!selectable.includes(kind)) return null

  return `/smart-cms?open=${encodeURIComponent(path)}`
}

function Trace({ worker }: { worker: CMSSearchWorker }) {
  const calls: NavCall[] = (worker as any).callLog ?? []
  const [openStep, setOpenStep] = useState<number | null>(null)

  if (!calls.length) {
    return (
      <div className='px-2 pb-2'>
        {(worker as any).searchingByMeaning && <SearchingByMeaning />}
        <p className='text-[10px] text-muted-foreground'>
          No calls yet. Run the agent and each CMS call will be listed here.
        </p>
      </div>
    )
  }

  const searching = !!(worker as any).searchingByMeaning

  // Newest first: the latest call is the one being watched.
  // and the panel is short enough that scrolling to the bottom is a nuisance.
  const ordered = [...calls].reverse()

  return (
    <div className='px-2 pb-2 nodrag'>
      <div className='flex items-center justify-between pb-1'>
        <Label className='text-[10px] text-muted-foreground'>Recent calls</Label>
        {searching && <SearchingByMeaning />}
        <span className='text-[10px] text-muted-foreground'>{calls.length}</span>
      </div>
      <div className={cn('overflow-y-auto rounded border border-gray-200 bg-white', openStep === null ? 'max-h-40' : 'max-h-[36rem]')}>
        {ordered.map(c => {
          const isOpen = openStep === c.step
          return (
            <div key={c.step} className='border-b border-gray-100 last:border-b-0'>
              <div className='flex items-start gap-1.5 px-1.5 py-1 hover:bg-gray-50'>
                <button
                  onClick={() => setOpenStep(isOpen ? null : c.step)}
                  className='flex min-w-0 grow items-start gap-1.5 text-left'
                >
                  <span className='w-4 shrink-0 tabular-nums text-[10px] text-gray-400'>{c.step}</span>
                  <span className='shrink-0 text-[10px] font-medium text-gray-700'>{c.action}</span>
                  {c.meaning && <SearchMode run={c.meaning} />}
                  <span className='min-w-0 grow truncate text-[10px] text-gray-500'>
                    {c.path || "/"}
                  </span>
                  <span className={cn(
                    'shrink-0 text-[10px]',
                    c.failed ? 'text-destructive' : 'text-emerald-600',
                  )}>
                    {c.outcome}
                  </span>
                </button>
                {/*
                  Opens the record in the Smart CMS. A CMS path is an address
                  within the CMS, not an app route -- following one as a URL
                  lands on nothing -- so it goes through ?open=, which
                  SmartCMSPage consumes to select the node.
                */}
                {cmsHref(c.path) && (
                  <a
                    href={cmsHref(c.path)!}
                    target='_blank'
                    rel='noreferrer'
                    title='Open in Smart CMS'
                    onClick={e => e.stopPropagation()}
                    className='mt-px shrink-0 text-gray-400 hover:text-gray-700'
                  >
                    <ExternalLink className='h-2.5 w-2.5' />
                  </a>
                )}
              </div>
              {/* The per-query breakdown: which table, how many rows, what
                  failed. This is what separates "searched the wrong place"
                  from "the content is not there". */}
              {isOpen && (
                <div className='space-y-1 px-1.5 pb-1.5 pl-6'>
                  {!!c.results?.length && <RankedResults results={c.results} />}
                  {/* Which query ran, against what, and what came back. */}
                  {c.steps.map((s, i) => (
                    <div key={i} className='flex gap-1.5 text-[10px]'>
                      <span className={s.error ? 'text-destructive' : 'text-gray-400'}>
                        {s.error ? '✗' : '·'}
                      </span>
                      <span className='text-gray-600'>{s.label}</span>
                      <span className='min-w-0 grow truncate text-gray-400'>
                        {s.error ?? `${s.rows} row${s.rows === 1 ? '' : 's'}${s.unpublished ? ` · ${s.unpublished} unpublished left out` : ''}`}
                      </span>
                    </div>
                  ))}
                  {/*
                    What the model actually received. Without it the panel
                    says a call returned "3 folders" but not which -- and for
                    a refusal, which has no query steps at all, the expansion
                    was previously empty.
                  */}
                  {c.result && (c.results?.length
                    ? <FullResponse text={c.result} failed={c.failed} />
                    : <ResultText text={c.result} failed={c.failed} />)}
                  {!c.steps.length && !c.result && (
                    <p className='text-[10px] text-gray-400'>No detail recorded for this call.</p>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export function CMSSearchNode(props: NodeProps) {
  const worker = useWorker<CMSSearchWorker>(props.id)

  return <NodeLayout worker={worker} resizable className="flex flex-col" minHeight={520} minWidth={350}>
    <WorkerLabeledHandle handler={worker.fields.tool} />
    <WorkerLabeledHandle handler={worker.fields.input} />
    <WorkerLabeledHandle handler={worker.fields.locale} />
    <WorkerLabeledHandle handler={worker.fields.maxResults} />
    <WorkerLabeledHandle handler={worker.fields.output} />
    <WorkerLabeledHandle handler={worker.fields.textOutput} />
    <WorkerLabeledHandle handler={worker.fields.references} />
    <ConditionHandler />
    <MemoizedWorker worker={worker}><Parameters worker={worker} /></MemoizedWorker>
    <MemoizedWorker worker={worker}><Trace worker={worker} /></MemoizedWorker>
  </NodeLayout>
}
